import Text from '../../graphics/Text';
import DraggablePreset from './DraggablePreset';
import Rectangle from '../../graphics/Rectangle';
import GraphicMask from '../../graphics/GraphicMask';
import {
  getCenterX,
  getCenterY,
  getFixBottomY,
  getFixLeftX,
  getFixRightX,
  getFixTopY,
  getXAccountingForAnchor,
  getYAccountingForAnchor,
} from '../../utils/CoordinateCalculator';

const MAX_REVEAL_DURATION = 1;

export default class BoxReveal extends DraggablePreset {
  constructor(app) {
    super(app);

    this.textMask = new GraphicMask();
    this.text = new Text(app);
    this.text.mask = this.textMask.mask;

    this.container.addChild(this.text, this.text.mask);

    this.boxLeft = new Rectangle();
    this.boxTop = new Rectangle();
    this.boxRight = new Rectangle();
    this.boxBottom = new Rectangle();
  }

  update(model) {
    super.update(model);

    const revealDuration = Math.min(this.duration / 2, MAX_REVEAL_DURATION);

    // update content and size
    this.text.text = model.text;
    this.text.updateStyle(model);

    const boxThickness = this.text.fontSize * 0.2;

    this.setDraggableRoot(
      this.text.lineControls.height + boxThickness * 4,
      this.text.lineControls.width + boxThickness * 4
    );

    this.boxLeft.color = this.secondaryColor;
    this.boxTop.color = this.secondaryColor;
    this.boxRight.color = this.secondaryColor;
    this.boxBottom.color = this.secondaryColor;

    // position
    this.text.lineControls.x = getCenterX(this.text.lineControls, this.root);
    this.text.lineControls.y = getCenterY(this.text.lineControls, this.root);

    this.textMask.width = 0;
    this.textMask.height = this.root.height;
    this.textMask.x = getCenterX(this.textMask, this.root);
    this.textMask.y = getFixTopY(this.textMask, this.root);

    this.boxLeft.width = boxThickness;
    this.boxLeft.height = 0;
    this.boxTop.width = 0;
    this.boxTop.height = boxThickness;
    this.boxRight.width = boxThickness;
    this.boxRight.height = 0;
    this.boxBottom.width = 0;
    this.boxBottom.height = boxThickness;

    this.boxLeft.x = getFixLeftX(this.boxLeft, this.root);
    this.boxLeft.y = getFixBottomY(this.boxLeft, this.root);
    this.boxTop.x = getFixLeftX(this.boxTop, this.root);
    this.boxTop.y = getFixTopY(this.boxTop, this.root);
    this.boxRight.x = getFixRightX(this.boxRight, this.root);
    this.boxRight.y = getFixTopY(this.boxRight, this.root);
    this.boxBottom.x = getFixRightX(this.boxBottom, this.root);
    this.boxBottom.y = getFixBottomY(this.boxBottom, this.root);

    const boxPerimeter = 2 * (this.root.width + this.root.height);
    const verticalDuration = (revealDuration * this.root.height) / boxPerimeter;
    const horizontalDuration =
      (revealDuration * this.root.width) / boxPerimeter;

    // draw left box side
    this.animate(this.boxLeft)
      .to({
        height: this.root.height,
        y: getYAccountingForAnchor(this.root),
        duration: verticalDuration,
      })
      .reverseOut();

    // reveal text once first box side is done drawing
    this.animate(this.textMask)
      .to(
        {
          x: getXAccountingForAnchor(this.root),
          width: this.root.width,
          duration: revealDuration - verticalDuration,
        },
        verticalDuration
      )
      .reverseOut();

    // draw top box side
    this.animate(this.boxTop)
      .to(
        {
          width: this.root.width,
          duration: horizontalDuration,
        },
        verticalDuration
      )
      .reverseOut();

    // draw right box side
    this.animate(this.boxRight)
      .to(
        {
          height: this.root.height,
          duration: verticalDuration,
        },
        revealDuration / 2
      )
      .reverseOut();

    // draw bottom box side
    this.animate(this.boxBottom)
      .to(
        {
          width: this.root.width,
          x: getXAccountingForAnchor(this.root),
          duration: horizontalDuration,
        },
        revealDuration - horizontalDuration
      )
      .reverseOut();
  }

  render() {
    super.render();
    this.boxLeft.draw(this.graphics);
    this.boxTop.draw(this.graphics);
    this.boxRight.draw(this.graphics);
    this.boxBottom.draw(this.graphics);

    this.textMask.render();
  }
}
